import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  inject,
  DestroyRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AppInput } from '../../../../shared/app-input/app-input';
import {
  AppDropdownComponent,
  DropdownOption,
} from '../../../../shared/app-dropdown/app-dropdown.component';
import { AppDatepicker } from '../../../../shared/app-datepicker/app-datepicker';

import {
  ApplicantField,
  SponsorshipQuestion,
  PolicyDetail,
  SingleApplicantData,
} from '../../../../shared/models/applicant-profile.model';

import {
  ApplicantDropdownOption,
  RELATIONSHIP_TO_SELF_OPTIONS,
  WHO_IS_SPONSOR_OPTIONS,
  SPONSOR_TYPE_OPTIONS,
  SPONSOR_EMIRATE_OPTIONS,
  SPONSOR_SALARY_BAND_OPTIONS,
} from '../../../../shared/models/applicant-profile.constants';
import { DateTimeConversionHelper } from '../../../../shared/helper/date-time-conversion.helper';

import { Banner } from '../../../../shared/banner/banner';
import { AppButtons } from '../../../../shared/app-buttons/app-buttons';

import { ApplicationReviewService } from '../../../services/application-review.service';
import { ToasterService } from '../../../../shared/toaster/toaster.service';
import { UtilsService } from '../../../../core/services/utils.service';
import { ApiResponse } from '../../../../core/models/global.model';

@Component({
  selector: 'app-applicant-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    AppInput,
    AppDropdownComponent,
    AppDatepicker,
    Banner,
    AppButtons,
  ],
  templateUrl: './applicant-profile.html',
  styleUrl: './applicant-profile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicantProfile implements OnChanges {
  /** Applicant data fed from application-review */
  profileData = input.required<SingleApplicantData>();
  leadInfoId = input.required<string>();
  finalSubmissionId = input<string>('');
  isReadOnly = input<boolean>(false);
  enableCopyMode = input<boolean>(false);

  /** Dropdown options */
  genderOptions = input<DropdownOption[]>([]);
  visaEmirateOptions = input<DropdownOption[]>([]);
  maritalStatusOptions = input<DropdownOption[]>([]);
  relationshipOptions = input<DropdownOption[]>([]);
  whoIsSponsorOptions = input<DropdownOption[]>([]);
  sponsorTypeOptions = input<DropdownOption[]>([]);
  sponsorEmirateOptions = input<DropdownOption[]>([]);
  salaryBandOptions = input<DropdownOption[]>([]);

  /** Outputs */
  isAdditionalInfoSaved = output<Record<string, unknown>>();
  editCancelled = output<void>();

  /** Section collapse state */
  collapsedSections: Record<string, boolean> = {
    basicInfo: false,
    additionalInfo: false,
  };

  /** Signals */
  additionalInfoEditMode = signal<boolean>(false);

  /** Form */
  additionalInfoForm: FormGroup;

  readonly minPolicyStartDate: Date = this.createTodayDate();
  readonly maxPolicyStartDate: Date =
    this.createMaxPolicyStartDate(this.minPolicyStartDate);

  /** Services */
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly utils = inject(UtilsService);
  private readonly applicationReviewService = inject(
    ApplicationReviewService
  );
  private readonly toaster = inject(ToasterService);
  private readonly clipboard = inject(Clipboard);
  private initialAdditionalInfoSnapshot: Record<string, unknown> | null = null;
  private previousApplicantIdentity: string | null = null;



  constructor() {
    this.additionalInfoForm = this.fb.group({
      relationshipToSelf: [{ value: ''}, Validators.required],
      sponsor: ['', Validators.required],
      sponsorName: [
        '',
        [Validators.required, this.utils.onlyAlphabetsValidator()],
      ],
      sponsorType: ['', Validators.required],
      sponsorEmirate: ['', Validators.required],
      sponsorSalaryBand: ['', Validators.required],
      policyStartDate: ['', Validators.required],
    });

    this.additionalInfoForm
      .get('relationshipToSelf')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateWhoIsSponsorControlState();
        this.updateSponsorNameControlState(false);
      });

    this.additionalInfoForm
      .get('sponsor')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateSponsorNameControlState();
      });

  }

  ngOnChanges(changes: SimpleChanges): void {
    const profileDataChange = changes['profileData'];
    const currentApplicantIdentity = this.getApplicantIdentity(
      this.profileData()
    );

    if (!profileDataChange || profileDataChange.firstChange) {
      this.previousApplicantIdentity = currentApplicantIdentity;
      return;
    }

    const hasApplicantChanged =
      !!currentApplicantIdentity &&
      !!this.previousApplicantIdentity &&
      currentApplicantIdentity !== this.previousApplicantIdentity;

    if (hasApplicantChanged) {
      this.additionalInfoEditMode.set(false);
      this.additionalInfoForm.markAsPristine();
      this.additionalInfoForm.markAsUntouched();
    }

    this.previousApplicantIdentity = currentApplicantIdentity;
  }



  toggleSection(sectionKey: string): void {
    this.collapsedSections[sectionKey] =
      !this.collapsedSections[sectionKey];
  }

  onEditClick(): void {
    if (this.isReadOnly()) {
      return;
    }

    const data = this.profileData();
    const questions = data.additionalInfo.sponsorshipQuestions;
    const policyDate =
      data.additionalInfo.policyDetails[0]?.value ?? '';

    this.additionalInfoForm.patchValue({
      relationshipToSelf: this.mapOptionValue(
        this.getQuestionValue(questions, 'Relationship to Self'),
        RELATIONSHIP_TO_SELF_OPTIONS
      ),

      sponsor: this.mapOptionValue(
        this.getQuestionValue(questions, 'Who is the Sponsor'),
        WHO_IS_SPONSOR_OPTIONS
      ),

      sponsorName: this.getQuestionValue(
        questions,
        'Sponsor Name / Employer Name'
      ),

      sponsorType: this.mapOptionValue(
        this.getQuestionValue(questions, 'Sponsor Type'),
        SPONSOR_TYPE_OPTIONS
      ),

      sponsorEmirate: this.mapOptionValue(
        this.getQuestionValue(questions, "Sponsor's Emirates"),
        SPONSOR_EMIRATE_OPTIONS
      ),

      sponsorSalaryBand: this.mapSalaryBandValue(
        this.getQuestionValue(questions, 'Sponsor Salary Band')
      ),

      policyStartDate: policyDate,
    }, { emitEvent: false });

    this.updateWhoIsSponsorControlState();
    this.updateSponsorNameControlState(false);

    this.initialAdditionalInfoSnapshot =
      this.getCurrentAdditionalInfoPayload();
    this.additionalInfoForm.markAsPristine();
    this.additionalInfoForm.markAsUntouched();

    this.additionalInfoEditMode.set(true);
  }

  onCancelEdit(): void {
    this.additionalInfoEditMode.set(false);
    this.editCancelled.emit();
  }

  onSave(): void {
    if (this.isReadOnly()) {
      return;
    }

    if (this.isSaveDisabled) {
      this.additionalInfoForm.markAllAsTouched();
      return;
    }



    const payload: {
      leadInfoId: string;
      FinalSubmissionId?: string;
      sponsorship: Record<string, unknown>;
    } = {
      leadInfoId: this.leadInfoId(),
      sponsorship: this.mapAdditionalInfoForApi(
        this.additionalInfoForm.getRawValue()
      ),
    };

    const submissionId = this.finalSubmissionId()?.trim();
    if (submissionId) {
      payload['FinalSubmissionId'] = submissionId;
    }

    this.applicationReviewService.subscribeWithCleanup(
      this.applicationReviewService.updateAdditionalInfo(payload),
      (_res: ApiResponse & { messages?: string }) => {
        const updatedPayload = this.getCurrentAdditionalInfoPayload();
        this.initialAdditionalInfoSnapshot = updatedPayload;
        this.additionalInfoForm.markAsPristine();
        this.isAdditionalInfoSaved.emit(updatedPayload);
        this.additionalInfoEditMode.set(false);
      },
      this.destroyRef
    );
  }

  get isSaveDisabled(): boolean {
    return (
      !this.additionalInfoEditMode() ||
      !this.areRequiredFieldsValid() ||
      !this.hasAdditionalInfoChanged()
    );
  }

  hasUnsavedChanges(): boolean {
    return (
      this.additionalInfoEditMode() &&
      this.hasAdditionalInfoChanged()
    );
  }

  getRelationshipOptions(): DropdownOption[] {
    return this.getOptionsOrFallback(
      this.relationshipOptions(),
      RELATIONSHIP_TO_SELF_OPTIONS
    );
  }

  getWhoIsSponsorOptions(): DropdownOption[] {
    return this.getOptionsOrFallback(
      this.whoIsSponsorOptions(),
      WHO_IS_SPONSOR_OPTIONS
    );
  }

  getSponsorTypeOptions(): DropdownOption[] {
    return this.getOptionsOrFallback(
      this.sponsorTypeOptions(),
      SPONSOR_TYPE_OPTIONS
    );
  }

  getSponsorEmirateOptions(): DropdownOption[] {
    return this.getOptionsOrFallback(
      this.sponsorEmirateOptions(),
      SPONSOR_EMIRATE_OPTIONS
    );
  }

  getSalaryBandOptions(): DropdownOption[] {
    return this.getOptionsOrFallback(
      this.salaryBandOptions(),
      SPONSOR_SALARY_BAND_OPTIONS
    );
  }

  private formatDisplayDate(value: string | null | undefined): string {
    const normalized = (value ?? '').trim();
    if (!normalized) return '';
    return DateTimeConversionHelper.formatDateForPayload(normalized) || normalized;
  }

  getBasicInfoFields(): ApplicantField[] {
    const info = this.profileData().basicInfo;

    const dobField = info.dob
      ? {
          ...info.dob,
          value: this.formatDisplayDate(info.dob.value),
        }
      : info.dob;

    return [
      info.firstName,
      info.lastName,
      dobField,
      info.gender,
      info.emirateOfVisaInsurance,
      info.maritalStatus,
    ];
  }

  getSponsorshipQuestions(): SponsorshipQuestion[] {
    return this.profileData().additionalInfo.sponsorshipQuestions;
  }

  getVisibleSponsorshipQuestions(): SponsorshipQuestion[] {
    let questions = this.getSponsorshipQuestions();

    if (!this.isDependentApplicant()) {
      questions = questions.filter(
        question => question.label !== 'Relationship to Self'
      );
    }

    if (this.isSponsorSelf()) {
      questions = questions.filter(
        question =>
          question.label !== 'Sponsor Name / Employer Name' &&
          question.label !== 'Sponsor Type' &&
          question.label !== "Sponsor's Emirates"
      );
    }

    return questions;
  }

  isDependentApplicant(): boolean {
    return this.profileData().isDependentApplicant ?? false;
  }

  isSponsorSelf(): boolean {
  const sponsorValue = this.getEffectiveSponsorValue();

  if (sponsorValue !== null) {
    return this.normalizeOptionKey(sponsorValue) ===
      this.normalizeOptionKey('Self');
  }

  const profileSponsorValue = this.getQuestionValue(
    this.getSponsorshipQuestions(),
    'Who is the Sponsor'
  );

  if (profileSponsorValue) {
    return this.normalizeOptionKey(profileSponsorValue) ===
      this.normalizeOptionKey('Self');
  }

  return this.profileData().isSponsorSelf ?? false;
}

  get isSponsorNameReadOnly(): boolean {
    return this.isSponsorSelf();
  }

  getPolicyDetails(): PolicyDetail[] {
    const details = this.profileData().additionalInfo.policyDetails ?? [];
    return details.map((detail) => ({
      ...detail,
      value: this.formatDisplayDate(detail.value),
    }));
  }

  isCommonAcrossApplicants(): boolean {
    return this.profileData().additionalInfo.isCommonAcrossApplicants;
  }

  get canCopyValues(): boolean {
    return this.isReadOnly() && this.enableCopyMode() && !this.additionalInfoEditMode();
  }

  onCopyValue(value: string, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (!this.canCopyValues) {
      return;
    }

    const normalizedValue = value?.trim() ?? '';
    if (!normalizedValue || normalizedValue === '-') {
      return;
    }

    if (!this.clipboard.copy(normalizedValue)) {
      return;
    }

    this.toaster.show({
      message: 'Copied to clipboard',
      status: 'success',
      position: 'top-right',
    });
  }

  private updateWhoIsSponsorControlState(): void {
    const relationshipValue =
      this.additionalInfoForm.get('relationshipToSelf')?.value;

    const sponsorControl =
      this.additionalInfoForm.get('sponsor');

    if (!sponsorControl) {
      return;
    }

    const isSelfOnlyApplicant =
      this.isSelfRelationship(relationshipValue);

    if (isSelfOnlyApplicant) {
      sponsorControl.enable({ emitEvent: false });
      return;
    }

    sponsorControl.disable({ emitEvent: false });
  }

  private updateSponsorNameControlState(
    shouldAutoFillForSelf: boolean = true
  ): void {
    const sponsorNameControl =
      this.additionalInfoForm.get('sponsorName');

    if (!sponsorNameControl) {
      return;
    }

    if (this.isSponsorSelf()) {
      if (shouldAutoFillForSelf) {
        const leadName = this.getLeadApplicantName();
        if (leadName) {
          sponsorNameControl.setValue(leadName, { emitEvent: false });
        }
      }

      sponsorNameControl.disable({ emitEvent: false });
      return;
    }

    sponsorNameControl.enable({ emitEvent: false });
  }

  private getLeadApplicantName(): string {
    const basicInfo = this.profileData().basicInfo;
    const firstName = basicInfo.firstName?.value?.trim() ?? '';
    const lastName = basicInfo.lastName?.value?.trim() ?? '';

    return `${firstName} ${lastName}`.trim();
  }

  private isSelfRelationship(value: unknown): boolean {
    return (
      typeof value === 'string' &&
      value.trim().toLowerCase() === 'self'
    );
  }

  private getApplicantIdentity(data: SingleApplicantData): string | null {
    const applicantId = data.basicInfo.applicantId?.trim();
    if (applicantId) {
      return `applicantId:${applicantId}`;
    }

    const basicInfoId = data.basicInfo.id?.trim();
    if (basicInfoId) {
      return `basicInfoId:${basicInfoId}`;
    }

    const firstName = data.basicInfo.firstName?.value?.trim() ?? '';
    const lastName = data.basicInfo.lastName?.value?.trim() ?? '';
    const dob = data.basicInfo.dob?.value?.trim() ?? '';

    if (!firstName && !lastName && !dob) {
      return null;
    }

    const applicantType = data.isDependentApplicant ? 'dependent' : 'primary';
    return `${applicantType}:${firstName}|${lastName}|${dob}`;
  }

  private getOptionsOrFallback(
    providedOptions: DropdownOption[],
    fallbackOptions: readonly ApplicantDropdownOption[]
  ): DropdownOption[] {
    return providedOptions.length > 0
      ? providedOptions
      : [...fallbackOptions];
  }

  private mapOptionValue(
    sourceValue: string,
    options: readonly ApplicantDropdownOption[]
  ): string {
    const trimmedValue = sourceValue.trim();

    if (!trimmedValue) {
      return '';
    }

    const normalizedInput =
      this.normalizeOptionKey(trimmedValue);

    const valueMatch = options.find(
      option =>
        this.normalizeOptionKey(option.value) ===
        normalizedInput
    );

    if (valueMatch) {
      return valueMatch.value;
    }

    const matchedOption = options.find(
      option =>
        this.normalizeOptionKey(option.label) ===
        normalizedInput
    );

    if (matchedOption) {
      return matchedOption.value;
    }

    return trimmedValue;
  }

  private mapSalaryBandValue(sourceValue: string): string {
    const normalizedValue = sourceValue
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

    if (
      normalizedValue.includes('4000') &&
      normalizedValue.includes('12000')
    ) {
      return SPONSOR_SALARY_BAND_OPTIONS[1].value;
    }

    if (
      normalizedValue.includes('>') ||
      normalizedValue.includes('above')
    ) {
      return SPONSOR_SALARY_BAND_OPTIONS[2].value;
    }

    if (
      normalizedValue.includes('<') ||
      normalizedValue.includes('below')
    ) {
      return SPONSOR_SALARY_BAND_OPTIONS[0].value;
    }

    return this.mapOptionValue(
      sourceValue,
      SPONSOR_SALARY_BAND_OPTIONS
    );
  }

  private mapAdditionalInfoForApi(
    formValue: Record<string, unknown>
  ): Record<string, unknown> {
    const preferredPolicyStartDate =
      this.formatPolicyStartDateForApi(
        formValue['policyStartDate']
      );

    const payload: Record<string, unknown> = {
      sponsor: this.normalizeNullable(formValue['sponsor']),
      sponsorName: this.normalizeNullable(
        formValue['sponsorName']
      ),
      sponsorSalaryBand: this.normalizeNullable(
        formValue['sponsorSalaryBand']
      ),
      preferredPolicyStartDate,
    };

    if (this.isDependentApplicant()) {
      payload['relationshipToSelf'] =
        this.normalizeNullable(
          formValue['relationshipToSelf']
        );
    }

    if (!this.isSponsorSelf()) {
      payload['sponsorType'] =
        this.normalizeNullable(formValue['sponsorType']);

      payload['sponsorEmirate'] =
        this.normalizeNullable(formValue['sponsorEmirate']);
    }

    return payload;
  }

  private getCurrentAdditionalInfoPayload(): Record<string, unknown> {
    return this.mapAdditionalInfoForApi(
      this.additionalInfoForm.getRawValue()
    );
  }

  private areRequiredFieldsValid(): boolean {
    const requiredControls: string[] = [
      'sponsorName',
      'sponsorSalaryBand',
      'policyStartDate',
    ];

    if (this.isDependentApplicant()) {
      requiredControls.push('relationshipToSelf');
    }

    const sponsorControl = this.additionalInfoForm.get('sponsor');
    if (sponsorControl?.enabled) {
      requiredControls.push('sponsor');
    }

    if (!this.isSponsorSelf()) {
      requiredControls.push('sponsorType', 'sponsorEmirate');
    }

    return requiredControls.every((controlName: string) =>
      this.isControlValidAndFilled(controlName)
    );
  }

  private isControlValidAndFilled(controlName: string): boolean {
    const control = this.additionalInfoForm.get(controlName);

    if (!control) {
      return false;
    }

    if (control.disabled) {
      return true;
    }

    if (control.invalid) {
      return false;
    }

    const controlValue = control.value;
    if (typeof controlValue === 'string') {
      return controlValue.trim().length > 0;
    }

    return controlValue !== null && controlValue !== undefined;
  }

  private hasAdditionalInfoChanged(): boolean {
    if (!this.initialAdditionalInfoSnapshot) {
      return false;
    }

    const currentPayload = this.getCurrentAdditionalInfoPayload();

    return (
      JSON.stringify(currentPayload) !==
      JSON.stringify(this.initialAdditionalInfoSnapshot)
    );
  }

  private normalizeOptionKey(value: string): string {
    return value.replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  private normalizeNullable(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    return normalized ? normalized : null;
  }

  private createTodayDate(): Date {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
  }

  private createMaxPolicyStartDate(fromDate: Date): Date {
    const maxDate = new Date(fromDate);
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate;
  }

  private getQuestionValue(
    questions: SponsorshipQuestion[],
    label: string
  ): string {
    return (
      questions.find(q => q.label === label)?.value ?? ''
    );
  }

  private getEffectiveSponsorValue(): string | null {
    const sponsorControl = this.additionalInfoForm.get('sponsor');
    const sponsorValue = sponsorControl?.value;

    if (typeof sponsorValue !== 'string') {
      return null;
    }

    const trimmedSponsorValue = sponsorValue.trim();
    return trimmedSponsorValue || null;
  }

  private formatPolicyStartDateForApi(
    value: unknown
  ): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }

      return this.toDateTimeString(value);
    }

    const rawText = String(value).trim();

    if (!rawText) {
      return null;
    }

    const dayFirstMatch = rawText.match(
      /^(\d{2})-(\d{2})-(\d{4})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );

    if (dayFirstMatch) {
      const [
        ,
        dd,
        mm,
        yyyy,
        hh = '00',
        min = '00',
        ss = '00',
      ] = dayFirstMatch;

      return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    }

    const parsedDate = new Date(rawText);

    if (!Number.isNaN(parsedDate.getTime())) {
      return this.toDateTimeString(parsedDate);
    }

    return null;
  }

  private toDateTimeString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }
}
